/**
 * The "validate shipment details" resolution path. Prefetches everything the cards on that screen
 * need in one call, so the merchant never waits per card, and remembers which cards were opened.
 * Owned by ResolveLabelErrorActionStore.
 */
import { makeAutoObservable, runInAction } from "mobx";
import RmaService from "../../../../../services/api/rma-service";
import { rootContainer, TYPE } from "../../../../../services/container/root";
import {
    IFetchShippingDetailsResponse,
    IFetchShippingRatesRequest,
    IResolveLabelErrorApproveRequest,
    IResolveLabelErrorQuotedApproveRequest,
    IResolveLabelErrorUnquotedApproveRequest,
} from "../../rma-types";
import RmaPageStore from "../rma-page-store";
import AddressStore from "./address-store";
import ParcelStore from "./parcel-store";
import ShippingRatesStore from "./shipping-rates-store";

class ValidateShippingDetailsStore {
    private _isLoading = false;
    private _loadFailed = false;
    private _shippingDetails: IFetchShippingDetailsResponse | null = null;
    /** Cards the merchant has expanded at least once while the modal stayed open. */
    private _reviewedCardIds = new Set<string>();
    /**
     * The two addresses of the label, each its own store so edits to one cannot reach the other.
     * Both are replaced by every load rather than refilled: the cards that read them are rendered
     * only once a load has succeeded, so opening the modal on another RMA cannot show its address.
     */
    private _customerAddress = new AddressStore(null);
    private _returnAddress = new AddressStore(null);
    /** The parcel the label is quoted on; replaced by every load for the same reason. */
    private _parcel = new ParcelStore(null);
    /**
     * Whether the corrected return address should be written back onto the return method, so the
     * next RMA on it starts from the fixed one. Off unless the merchant asks for it: the address
     * they typed may be a one-off for this parcel.
     */
    private _shouldSaveReturnAddress = false;
    /** Kept across a load: the rates are quoted on the details, not fetched with them. */
    private _shippingRates = new ShippingRatesStore();
    private _isQuoting = false;
    private _isApproving = false;

    constructor(private _rma: RmaPageStore) {
        /**
         * isCardReviewed stays a plain method: as an action its reads would run untracked and the
         * reviewed marker would not appear until something else re-rendered the card.
         */
        makeAutoObservable(this, { isCardReviewed: false }, { autoBind: true });
    }

    private get _service(): RmaService { return rootContainer.get<RmaService>(TYPE.RmaService); }

    public get isLoading(): boolean { return this._isLoading; }

    /** True when the last load failed — the cards have nothing trustworthy to show. */
    public get loadFailed(): boolean { return this._loadFailed; }

    /** Everything the cards on the validate screen read; null until a load succeeds. */
    public get shippingDetails(): IFetchShippingDetailsResponse | null { return this._shippingDetails; }

    /** The address the carrier rejected, editable in place. */
    public get customerAddress(): AddressStore { return this._customerAddress; }

    /** The warehouse address the parcel goes back to, editable in place. */
    public get returnAddress(): AddressStore { return this._returnAddress; }

    /** The weights and dimensions the carrier quoted on, editable in place. */
    public get parcel(): ParcelStore { return this._parcel; }

    /** What the carrier quoted for these details, and which of them the merchant picked. */
    public get shippingRates(): ShippingRatesStore { return this._shippingRates; }

    /** True while the carrier is being re-quoted on the edited details. */
    public get isQuoting(): boolean { return this._isQuoting; }

    /** True while the approve that regenerates the label is in flight. */
    public get isApproving(): boolean { return this._isApproving; }

    /**
     * Every section the carrier is quoted on, opened or not, in the keys the prefetch answered with:
     * one left alone goes back exactly as it was read. Taken from the stores that hold the edits.
     */
    public get shippingRatesRequest(): IFetchShippingRatesRequest {
        return {
            customer_address: this._customerAddress.addressRequest,
            return_address: this._returnAddress.addressRequest,
            parcel: this._parcel.parcelRequest,
            items: this._parcel.itemsRequest,
        };
    }

    /**
     * Whether a carrier quotes the RMA's own method, so the edited details go to a rate before they
     * are approved. The prefetch answers it for that method. Read only once the prefetch is in: the
     * action it decides is rendered with the cards it fills.
     */
    public get isQuotableMethod(): boolean {
        return this._shippingDetails!.current_return_method.supports_shipping_rates;
    }

    public get shouldSaveReturnAddress(): boolean { return this._shouldSaveReturnAddress; }

    /**
     * The return method the corrected address would be written back to, named on the checkbox that
     * offers it - the merchant is changing a setting other RMAs use, and has to see which one.
     */
    public get returnMethodName(): string | undefined {
        return this._rma.rmaReturnMethod?._internal_name;
    }

    public setShouldSaveReturnAddress(shouldSave: boolean): void {
        this._shouldSaveReturnAddress = shouldSave;
    }

    public isCardReviewed(cardId: string): boolean {
        return this._reviewedCardIds.has(cardId);
    }

    public markCardReviewed(cardId: string): void {
        this._reviewedCardIds.add(cardId);
    }

    /** Drops what the previous RMA left behind, then refetches for the RMA it belongs to. */
    public async load(): Promise<void> {
        this._isLoading = true;
        this._loadFailed = false;
        this._shippingDetails = null;
        this._reviewedCardIds = new Set<string>();
        this._shouldSaveReturnAddress = false;
        this._shippingRates.reset();

        try {
            const shippingDetails = await this._service.fetchShippingDetails(this._rma.rmaId);
            runInAction(() => {
                this._shippingDetails = shippingDetails;
                this._customerAddress = new AddressStore(
                    shippingDetails.customer_address,
                    shippingDetails.countries,
                );
                this._returnAddress = new AddressStore(
                    shippingDetails.return_address,
                    shippingDetails.countries,
                );
                this._parcel = new ParcelStore(
                    shippingDetails.parcel,
                    shippingDetails.items,
                    shippingDetails.item_weights_required,
                );
            });
        } catch (e) {
            console.error("Failed to load shipping details for the resolve label error modal", e);
            runInAction(() => { this._loadFailed = true; });
        } finally {
            runInAction(() => { this._isLoading = false; });
        }
    }

    /**
     * Re-quotes the carrier on what the merchant edited. A quote that fails and one that comes back
     * empty are the same answer to them - this path cannot produce a label - so both end as failed
     * and the flow offers the other path instead.
     */
    public async fetchShippingRates(): Promise<void> {
        this._isQuoting = true;
        this._shippingRates.reset();

        try {
            const rates = await this._service.fetchShippingRates(this._rma.rmaId, this.shippingRatesRequest);
            runInAction(() => { this._shippingRates.apply(rates ?? []); });
        } catch (e) {
            console.error("Failed to fetch shipping rates for the resolve label error modal", e);
            runInAction(() => { this._shippingRates.fail(); });
        } finally {
            runInAction(() => { this._isQuoting = false; });
        }
    }

    /**
     * Approves the RMA on the edited details, which regenerates the label from them - on the chosen
     * rate, or on the method's own settings when no carrier quotes it. Throws on failure: the caller
     * owns what the merchant is told and what happens to the modal.
     */
    public async approve(): Promise<void> {
        const request = this._approveRequest();

        this._isApproving = true;

        try {
            await this._service.approve(this._rma.rmaId, request, { silent: true });
        } finally {
            runInAction(() => { this._isApproving = false; });
        }
    }

    /** The four sections and what goes with them; the rate only for a method a carrier quotes. */
    private _approveRequest(): IResolveLabelErrorApproveRequest {
        const request: IResolveLabelErrorUnquotedApproveRequest = {
            ...this.shippingRatesRequest,
            save_return_address: this._shouldSaveReturnAddress,
            /** The customer heard about this return already; fixing our label is not their news. */
            send_email_notification: false,
        };

        if (!this.isQuotableMethod) {
            return request;
        }

        const selectedRate = this._shippingRates.selectedRate;

        if (!selectedRate) {
            throw new Error("Approve was asked for before a rate was chosen");
        }

        return {
            ...request,
            selected_rate_id: selectedRate.id,
        } satisfies IResolveLabelErrorQuotedApproveRequest;
    }
}

export default ValidateShippingDetailsStore;
